// eslint-disable-next-line no-restricted-imports
import dayjs, { Dayjs, isDayjs, ManipulateType, PluginFunc } from 'dayjs';
import AdvancedFormatPlugin from 'dayjs/plugin/advancedFormat';
import IsSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import QuarterOfYear from 'dayjs/plugin/quarterOfYear';
import RelativeTimePlugin from 'dayjs/plugin/relativeTime';
import TimezonePlugin from 'dayjs/plugin/timezone'; // dependent on utc plugin
import UtcPlugin from 'dayjs/plugin/utc';
import { DateRange } from '../types';
import { TickRange } from '../valueSeries';

// Extends with RelativeTime plugin to allow .fromNow(), .from(), etc
// Details: https://day.js.org/docs/en/plugin/relative-time
dayjs.extend(RelativeTimePlugin);

// Extends with QuarterOfYear plugin to allow .quarter()
dayjs.extend(QuarterOfYear);

dayjs.extend(IsSameOrBefore);

// Extends with AdvancedFormat plugin to allow "th", "rd", etc
dayjs.extend(AdvancedFormatPlugin);

// Timezone adds `dayjs.tz` `.tz` `.tz.guess` `.tz.setDefault` APIs to parse
// or display between time zones.
// Details: https://day.js.org/docs/en/plugin/timezone
dayjs.extend(UtcPlugin);
dayjs.extend(TimezonePlugin);

// Add custom logic here
// Ref: https://day.js.org/docs/en/plugin/plugin
declare module 'dayjs' {
    interface Dayjs {
        toDateStr(): string;
        fromNowDays(): string;
        fromNow(withoutSuffix?: boolean): string;
    }
}
const customPlugins: PluginFunc = (
    _option,
    dayjsClass
    // _dayjsFactory
) => {
    // Add "toDateStr"
    // This method formats the DT as a canonical date string in our app.
    // This format is expected by all date-related logic on the server.
    dayjsClass.prototype.toDateStr = function (): string {
        return this.format('YYYY-MM-DD');
    };

    dayjsClass.prototype.fromNowDays = function (): string {
        const dtTodayStart = dt().startOf('day');
        const hoursFromDayStart = this.diff(dtTodayStart, 'hours');

        if (hoursFromDayStart < 0 && hoursFromDayStart > -24) {
            return 'yesterday';
        }
        if (hoursFromDayStart >= 0 && hoursFromDayStart < 24) {
            return 'today';
        }
        if (hoursFromDayStart >= 24 && hoursFromDayStart < 48) {
            return 'tomorrow';
        }

        return this.startOf('day').from(dtTodayStart);
    };
};
dayjs.extend(customPlugins);

export type DT = Dayjs;
export const dt = dayjs;
export const isDT = isDayjs;

export const dateDiff = (
    laterDate: string,
    earlierDate: string,
    unit: ManipulateType = 'days'
) => {
    return dt(laterDate).diff(earlierDate, unit);
};

export const isPastDate = (
    date: string,
    currentDate: string = dt().toDateStr()
): boolean => {
    return dt(date).toDateStr() < currentDate;
};

export const getUSPacificDisplayDateFromEpochSeconds = (
    seconds: number
): `${number}/${number}/${number}` =>
    dt(seconds * 1000)
        .tz('America/Los_Angeles')
        .startOf('day')
        .format('MM/DD/YYYY') as `${number}/${number}/${number}`;

export const getRangeUnit = (
    dateRange: DateRange
): 'day' | 'month' | 'year' | 'quarter' | 'custom' => {
    const startDate = dt(dateRange.startDate);
    const endDate = dt(dateRange.endDate);

    if (
        startDate.month() === endDate.month() &&
        startDate.year() === endDate.year() &&
        startDate.date() === startDate.startOf('month').date() &&
        endDate.date() === endDate.endOf('month').date()
    ) {
        return 'month';
    } else if (
        startDate.month() === 0 &&
        endDate.month() === 11 &&
        startDate.year() === endDate.year() &&
        startDate.date() === startDate.startOf('year').date() &&
        endDate.date() === endDate.endOf('year').date()
    ) {
        return 'year';
    } else if (
        startDate.year() === endDate.year() &&
        startDate.date() === startDate.startOf('quarter').date() &&
        endDate.date() === endDate.endOf('quarter').date()
    ) {
        return 'quarter';
    } else {
        return 'custom';
    }
};

export const getEarliestDate = (d1: string, d2?: string) =>
    !d2 || d1 < d2 ? d1 : d2;

export const minDate = (dates: string[]) => {
    if (!dates.length) {
        return null;
    }
    return dates.reduce(getEarliestDate);
};

export const ALL_TIME_DATE_RANGE = {
    // An arbitrary date very far in the past
    // A bit of a hack, but we can compare against this in other places to determine if the date range is "all time"
    startDate: dt().startOf('year').subtract(30, 'year').toDateStr(),
    endDate: dt().endOf('month').toDateStr(),
};

export const isAllTimeDateRange = (dateRange: DateRange) =>
    dateRange.startDate === ALL_TIME_DATE_RANGE.startDate &&
    dateRange.endDate === ALL_TIME_DATE_RANGE.endDate;

// TODO: Implement custom date range
export type DateRangeType =
    | 'this_month'
    | 'last_month'
    | 'last_30'
    | 'last_90'
    | 'this_year'
    | 'last_year'
    | 'all_time';

export const DEFAULT_DATE_RANGE_TYPE: DateRangeType = 'this_month';

export const tickRangeFromDateRangeType = (value: DateRangeType): TickRange => {
    const now = dt();
    switch (value) {
        case 'this_month':
            return {
                startDate: dt().startOf('month').toDateStr(),
                endDate: dt().endOf('month').toDateStr(),
                unit: 'day',
            };
        case 'last_month':
            return {
                startDate: dt()
                    .subtract(1, 'month')
                    .startOf('month')
                    .toDateStr(),
                endDate: dt().subtract(1, 'month').endOf('month').toDateStr(),
                unit: 'day',
            };
        case 'last_30':
            return {
                startDate: dt().subtract(30, 'day').toDateStr(),
                endDate: now.toDateStr(),
                unit: 'day',
            };
        case 'last_90':
            return {
                startDate: dt().subtract(90, 'day').toDateStr(),
                endDate: now.toDateStr(),
                unit: 'day',
            };
        case 'this_year':
            return {
                startDate: dt().startOf('year').toDateStr(),
                endDate: now.endOf('month').toDateStr(),
                unit: 'month',
            };
        case 'last_year':
            return {
                startDate: dt().subtract(1, 'year').startOf('year').toDateStr(),
                endDate: dt().subtract(1, 'year').endOf('year').toDateStr(),
                unit: 'month',
            };
        case 'all_time':
            return {
                ...ALL_TIME_DATE_RANGE,
                unit: 'month',
            };
        default:
            throw new Error(`Invalid date range option: ${value}`);
    }
};

export const durationStrByDateRangeType: Record<DateRangeType, string> = {
    this_month: ` in ${dt().format('MMMM')}`,
    last_month: ` in ${dt().subtract(1, 'month').format('MMMM')}`,
    last_30: `, last 30 days`,
    last_90: `, last 90 days`,
    this_year: ` in ${dt().format('YYYY')}`,
    last_year: ` in ${dt().subtract(1, 'year').format('YYYY')}`,
    all_time: ` all time`,
};

export const labelByDateRangeType: Record<DateRangeType, string> = {
    this_month: 'This month',
    last_month: 'Last month',
    last_30: 'Last 30 days',
    last_90: 'Last 90 days',
    this_year: 'This year',
    last_year: 'Last year',
    all_time: 'All time',
};

const dateRangeTypes = [
    'this_month',
    'last_month',
    'last_30',
    'last_90',
    'this_year',
    'last_year',
] as const;

export const DATE_RANGE_TYPE_OPTIONS = dateRangeTypes.map((value) => ({
    value,
    label: labelByDateRangeType[value],
}));

export const getTotalMonths = (dateRange?: DateRange) =>
    dateRange
        ? Math.round(
              dt(dateRange.endDate).diff(dateRange.startDate, 'months', true)
          )
        : 1;
